import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup, Button, UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { capitalize, upperCase } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useSlice from '~/utils/slice/hook';

const availableSlices = [
  'all',
  'system',
  'market',
  'organization',
];

const SLICE_LINEAGE_KEY = {
  market: 'market',
  specialty: 'specialty',
  'sub-specialty': 'subSpecialty',
  organization: 'organization',
  division: 'division',
};

const SliceMenu = ({
  slice, id: parentId, disabled, path,
}) => {
  const {
    fetch, currentSlice, setCurrentSlice, calculateLineage,
  } = useSlice();
  const [slices, setSlices] = useState([]);
  const [id, setId] = useState();
  const [label, setLabel] = useState();
  const [status, setStatus] = useState('loading');
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    setStatus('loading');
    setSlices([]);

    if (parentId) {
      const { prev } = calculateLineage(slice);
      const parentKey = prev === 'organization' ? 'org' : prev;

      fetch(slice, { [`${parentKey}-uid`]: parentId })
        .then(({ data: { data: r } }) => {
          setSlices(r);
          setStatus('success');
        });
    } else if (slice === 'market' || slice === 'organization') {
      fetch(slice)
        .then(({ data: { data: r } }) => {
          setSlices(r);
          setStatus('success');
        });
    }
  }, [parentId]);

  useEffect(() => {
    if (restored || !slices.length || !currentSlice.lineage) return;

    const lineageKey = SLICE_LINEAGE_KEY[slice];
    const targetId = lineageKey && currentSlice.lineage[lineageKey];
    if (!targetId) return;

    const match = slices.find(s => s.id === targetId);
    if (!match) return;

    setLabel(match.label);

    const { next } = calculateLineage(slice);
    if (next) {
      setId(match.id);
    }

    const newPath = { [slice]: match.id };
    path.setPath(p => ({ ...p, ...newPath, current: slice }));

    setRestored(true);
  }, [slices, currentSlice.lineage]);

  return (
    <>
      <UncontrolledButtonDropdown
        className="w-100"
        disabled={disabled}
      >
        <DropdownToggle
          caret
          active={slice === currentSlice.type}
          color={slice}
          outline={currentSlice.type !== slice}
          disabled={disabled}
        >
          {label &&
            Object.keys(path.path).some(p => calculateLineage(slice).lineage.includes(p))
            ? (
              <span>{label} /{' '}
                <small>
                  {upperCase(slice
                  .replace(/-/g, ' ')
                  .split(' ')
                  .map(w =>
                    w.split('')[0])
                  .join(''))
                }
                </small>
              </span>
            ) : capitalize(slice).replace(/-/g, ' ')}
        </DropdownToggle>

        <DropdownMenu
          style={{ zIndex: 9001 }}
          modifiers={{
            setMax: {
              enabled: true,
              order: 890,
              fn: data => ({
                  ...data,
                  styles: {
                    ...data.styles,
                    overflow: 'auto',
                    maxHeight: '50vh',
                  },
                }),
            },
          }}
        >
          {slices.map(s => (
            <DropdownItem
              key={s.id}
              active={s.id === currentSlice.id}
              className="d-flex"
              onClick={() => {
                setId(calculateLineage(slice).next ? s.id : null);
                setLabel(s.label);

                const newPath = { [slice]: s.id };

                if (Object.keys(path.path).includes(calculateLineage(slice).prev)) {
                  path.setPath({ ...path.path, ...newPath, current: slice });
                } else {
                  path.setPath({ ...newPath, current: slice });
                }

                setCurrentSlice({
                  type: slice,
                  id: s.id,
                  lineage: {
                    market: s.attributes.marketUid,
                    specialty: s.attributes.specialtyUid,
                    subSpecialty: s.attributes.subSpecialtyUid,
                    organization: s.attributes.orgUid,
                    division: s.attributes.divisionUid,
                  },
                });
              }}
            >
              <div className="mr-auto">
                {s.label}
              </div>

              {calculateLineage(slice).next &&
                <div className="ml-auto pl-3">
                  <FontAwesomeIcon icon="chevron-right" />
                </div>
              }
            </DropdownItem>
          ))}

          {status === 'success' && slices.length === 0 && (
            <DropdownItem>
              No Results found
            </DropdownItem>
          )}
        </DropdownMenu>
      </UncontrolledButtonDropdown>

      {
        calculateLineage(slice).next &&
        <SliceMenu
          slice={calculateLineage(slice).next}
          id={id}
          disabled={!id}
          path={path}
        />
      }
    </>
  );
};

SliceMenu.propTypes = {
  slice: PropTypes.string.isRequired,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  path: PropTypes.shape({
    path: PropTypes.shape(),
    setPath: PropTypes.func,
  }).isRequired,
};

SliceMenu.defaultProps = {
  id: undefined,
  disabled: false,
};

const SliceItem = ({ slice, global, path }) => {
  const { currentSlice, setCurrentSlice } = useSlice();

  return global
    ? (
      <Button
        onClick={() => { path.setPath({ [slice]: null }); setCurrentSlice(slice); }}
        outline={currentSlice.type !== slice}
        color={slice}
        className="w-100"
      >
        {capitalize(slice)}
      </Button>
    ) : (
      <SliceMenu slice={slice} path={path} />
    );
};

SliceItem.propTypes = {
  slice: PropTypes.string.isRequired,
  global: PropTypes.bool,
  path: PropTypes.shape().isRequired,
};

SliceItem.defaultProps = {
  global: false,
};

const SlicePicker = ({ slices }) => {
  const [path, setPath] = useState({});

  return (
    <ButtonGroup className="mb-3 w-100">
      {slices.includes('all') && <SliceItem
        slice="all"
        path={{ path, setPath }}
        global
      />}

      {slices.includes('system') && <SliceItem
        slice="system"
        path={{ path, setPath }}
        global
      />}

      {slices.includes('market') && <SliceItem
        slice="market"
        path={{ path, setPath }}
      />}

      {slices.includes('organization') && <SliceItem
        slice="organization"
        path={{ path, setPath }}
      />}
    </ButtonGroup>
  );
};

SlicePicker.propTypes = {
  slices: PropTypes.arrayOf(PropTypes.oneOf(availableSlices)),
};

SlicePicker.defaultProps = {
  slices: availableSlices,
};

export default SlicePicker;
